import React, { useEffect, useState } from 'react';
import s from './s.module.less';
import { EBillingStatus, TPaymentBilling } from 'types/provider';
import { getBillingStatusDescription, getBillingStatusLabel } from 'utils/common';
import { Popover } from 'antd';
import { getTextVisitRefundedInfo } from 'api/textVisit';
import dayjs from 'utils/dayjs';

interface Props {
    value?: EBillingStatus;
    isTextVisit?: boolean;
    billingId?: number;
    isInvoice?: boolean;
    item?: TPaymentBilling;
}

interface RefundedInfo {
    refundAmount: number;
    refundReason: string;
    notesToProvider: string;
}

const StatusLabel = ({ value, isTextVisit, billingId, isInvoice, item }: Props) => {
    const getInvoiceStatusDescription = (status?: EBillingStatus) => {
        if (!status) {
            return '';
        }
        const dueDate = dayjs(item?.dueDate).format('MM/DD/YYYY') || '';
        const scheduledDate = dayjs(item?.issueDate).format('MM/DD/YYYY') || '';

        const statusDescriptions = {
            [EBillingStatus.SCHEDULED]: `This invoice has been created and will be sent to the patient on ${scheduledDate}.`,
            [EBillingStatus.SENT]: 'This invoice has been sent to the patient, but payment has not been received yet.',
            [EBillingStatus.PARTIAL_PAID]:
                'A portion of this invoice has been paid. The remaining balance will be charged later.',
            [EBillingStatus.FULLY_PAID]: 'The invoice has been fully paid.',
            [EBillingStatus.REFUND_PROCESSING]:
                'A refund for this invoice is being processed. The status will update once the refund is completed.',
            [EBillingStatus.PARTIAL_REFUNDED]: 'The invoice has been partially refunded.',
            [EBillingStatus.REFUNDED]: 'The invoice has been fully refunded.',
            [EBillingStatus.REFUND_FAILED]: 'The refund attempt for this invoice was unsuccessful.',
            [EBillingStatus.OVERDUE]: `The due date ${dueDate} has passed and payment has not been received.`,
            [EBillingStatus.CANCELED]: 'This invoice was canceled and no payment is required.',
            [EBillingStatus.PAYMENT_FAILED]: 'A payment attempt for this invoice was unsuccessful. The outstanding balance remains, and the system or provider can retry collection.',
        };
        return statusDescriptions[status as keyof typeof statusDescriptions] || getBillingStatusDescription(status);
    };

    const getStatusColor = (status?: EBillingStatus) => {
        if (!status) {
            return s.statusTagDefault;
        }
        const statusColors = {
            [EBillingStatus.DISPUTE_OPEN]: s.statusTagError,
            // [EBillingStatus.DISPUTE_CLOSED]: s.statusTagSuccess,
            [EBillingStatus.PENDING]: s.statusTagProcessing,
            // [EBillingStatus.PAID]: s.statusTagSuccess,
            [EBillingStatus.FAILED]: s.statusTagError,
            [EBillingStatus.NOT_READY]: s.statusTagProcessing,
            [EBillingStatus.CANCELED]: '',
            [EBillingStatus.BILLING_CANCELED]: '',
            // [EBillingStatus.NOT_READY]: s.statusTagProcessing,
            [EBillingStatus.PROCESSING]: s.statusTagProcessing,
            [EBillingStatus.SUCCEEDED]: s.statusTagSuccess,
            [EBillingStatus.REFUND_PROCESSING]: s.statusTagProcessing,
            [EBillingStatus.REFUNDED]: s.statusTagWarning,
            [EBillingStatus.REFUND_FAILED]: s.statusTagError,
            [EBillingStatus.DISPUTE_PROCESSING]: s.statusTagProcessing,
            [EBillingStatus.DISPUTE_WON]: s.statusTagSuccess,
            [EBillingStatus.DISPUTE_LOST]: s.statusTagError,
            [EBillingStatus.PATIENT_NOT_READY]: s.statusTagProcessing,
            [EBillingStatus.PROVIDER_NOT_READY]: s.statusTagProcessing,
            // invoice
            [EBillingStatus.SCHEDULED]: s.statusTagProcessing,
            [EBillingStatus.SENT]: s.statusTagInfo,
            [EBillingStatus.PARTIAL_PAID]: s.statusTagSuccess,
            [EBillingStatus.FULLY_PAID]: s.statusTagSuccess,
            [EBillingStatus.OVERDUE]: s.statusTagError,
            [EBillingStatus.PARTIAL_REFUNDED]: s.statusTagWarning,
            // PAYMENT_FAILED is an invoice-level event recorded per failed payment attempt
            // (invoice.status itself stays PARTIAL_PAID). Use the same error red as FAILED.
            [EBillingStatus.PAYMENT_FAILED]: s.statusTagError,
        };
        return statusColors[status] || s.statusTagDefault;
    };
    const colorClass = getStatusColor(value);
    const des = isInvoice ? getInvoiceStatusDescription(value) : getBillingStatusDescription(value);
    const [refundedInfo, setRefundedInfo] = useState<RefundedInfo>();

    useEffect(() => {
        const fetchRefundedInfo = async () => {
            if (!billingId) return;
            try {
                const res = await getTextVisitRefundedInfo(billingId);
                if (res?.data?.data) {
                    setRefundedInfo(res.data.data);
                }
            } catch (error) {
                console.error(error);
            }
        };
        if (isTextVisit && value === EBillingStatus.REFUNDED && billingId) {
            fetchRefundedInfo();
        }
    }, [isTextVisit, value, billingId]);

    const refundedContent = () => {
        return (
            <div className="flex flex-col gap-1">
                <div>
                    Refunded:<span className="font-bold ml-1">${(refundedInfo?.refundAmount || 0).toFixed(2)}</span>
                </div>
                {refundedInfo?.refundReason && (
                    <div>
                        Refund Reason:<span className="font-bold ml-1">{refundedInfo?.refundReason}</span>
                    </div>
                )}
                {refundedInfo?.notesToProvider && (
                    <div>
                        Notes from Klarity team:<span className="font-bold ml-1">{refundedInfo?.notesToProvider}</span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`${s.statusTag} ${colorClass}`}>
            {getBillingStatusLabel(value)}
            <Popover
                content={
                    value === EBillingStatus.REFUNDED && isTextVisit ? (
                        refundedContent()
                    ) : (
                        <div className={s.pop}>{des}</div>
                    )
                }
                title={null}
                placement="right"
                trigger="hover"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                        d="M5.48511 6C5.85118 5.22321 6.83895 4.66667 8.00004 4.66667C9.4728 4.66667 10.6667 5.5621 10.6667 6.66667C10.6667 7.59963 9.81496 8.38338 8.66285 8.6044C8.30125 8.67377 8.00004 8.96514 8.00004 9.33333M8 11.3333H8.00667M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Popover>
        </div>
    );
};

export default StatusLabel;
